/**
 * Preview audio: one HTMLAudioElement slaved to the timeline clock.
 *
 * Deliberately NOT WebAudio. WebAudio (OfflineAudioContext) is for export
 * only; an <audio> element buffers itself, is cheap, and an 80 ms drift
 * tolerance is well inside what anyone hears in a preview.
 *
 * The clock is driven by requestAnimationFrame, which stops in a background
 * tab while an <audio> element would keep playing. So audio pauses when the
 * tab hides and is re-seeked on the first update after it returns.
 */

import type { AudioTrack } from '@canvas/shared-types';
import { gainAt, sourceMsAt } from './audioTrack';
import { resolvePlayableUrl } from './videoAssetUpload';

/** Re-seek when the element drifts further than this from the clock. */
export const AUDIO_DRIFT_TOLERANCE_MS = 80;
/** How often drift is checked while playing. */
const DRIFT_CHECK_MS = 500;

export interface AudioPreviewState {
  playing: boolean;
  /** Global timeline position. */
  timelineMs: number;
  totalMs: number;
  muted: boolean;
}

export interface TimelineAudioPreview {
  setTrack(track: AudioTrack | null): void;
  update(state: AudioPreviewState): void;
  destroy(): void;
}

function trackKey(track: AudioTrack | null): string {
  return track ? `${track.trackId}|${track.idbKey ?? ''}|${track.storagePath ?? ''}` : '';
}

export function createTimelineAudioPreview(): TimelineAudioPreview {
  const el = new Audio();
  el.preload = 'auto';
  let track: AudioTrack | null = null;
  let key = '';
  let url: string | null = null;
  let lastCheck = 0;
  let needsSeek = true;
  let state: AudioPreviewState | null = null;
  let destroyed = false;

  const releaseUrl = () => {
    if (url?.startsWith('blob:')) URL.revokeObjectURL(url);
    url = null;
  };

  const pause = () => {
    if (!el.paused) el.pause();
    needsSeek = true;
  };

  const apply = () => {
    if (!state || !track || !url || destroyed) {
      pause();
      return;
    }
    const { playing, timelineMs, totalMs, muted } = state;
    const src = sourceMsAt(track, totalMs, timelineMs);
    if (!playing || src === null || document.hidden) {
      pause();
      return;
    }
    el.volume = muted ? 0 : Math.min(1, Math.max(0, gainAt(track, totalMs, timelineMs)));
    const now = performance.now();
    const drifted = Math.abs(el.currentTime * 1000 - src) > AUDIO_DRIFT_TOLERANCE_MS;
    if (needsSeek || el.paused || (now - lastCheck >= DRIFT_CHECK_MS && drifted)) {
      if (needsSeek || drifted) el.currentTime = src / 1000;
      lastCheck = now;
      needsSeek = false;
      // Autoplay rejections are fine: the next user gesture's update retries.
      if (el.paused) void el.play().catch(() => undefined);
    }
  };

  const onVisibility = () => {
    if (document.hidden) pause();
    else apply();
  };
  document.addEventListener('visibilitychange', onVisibility);

  return {
    setTrack(next) {
      track = next;
      const nextKey = trackKey(next);
      if (nextKey === key) {
        apply();
        return;
      }
      key = nextKey;
      pause();
      releaseUrl();
      el.removeAttribute('src');
      el.load();
      if (!next) return;
      const want = nextKey;
      void resolvePlayableUrl(next).then((resolved) => {
        if (destroyed || key !== want) {
          if (resolved?.startsWith('blob:')) URL.revokeObjectURL(resolved);
          return;
        }
        url = resolved;
        if (resolved) el.src = resolved;
        needsSeek = true;
        apply();
      });
    },

    update(next) {
      state = next;
      apply();
    },

    destroy() {
      if (destroyed) return;
      destroyed = true;
      document.removeEventListener('visibilitychange', onVisibility);
      el.pause();
      el.removeAttribute('src');
      el.load();
      releaseUrl();
    },
  };
}
